'use client';

import type { FAQItem, FAQProps } from '@/core/types';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { m, AnimatePresence } from 'framer-motion';

import { Iconify } from '@/components/iconify';
import { Box, Container } from '@/components/ui';

export default function FAQ({ faqs, className = '' }: FAQProps) {
  const [expanded, setExpanded] = useState<number | false>(
    faqs.find((faq) => faq.isExpanded)?.id || false
  );

  const handleChange = (panel: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    setExpanded(expanded === panel ? false : panel);
  };

  return (
    <Box component="section" className={twMerge('w-full space-y-4', className)}>
      <Container className="w-full space-y-4">
        {faqs.map((faq) => (
          <FaqItem
            key={faq.id}
            item={faq}
            expanded={expanded === faq.id}
            onChange={handleChange(faq.id)}
          />
        ))}
      </Container>
    </Box>
  );
}

type FaqItemProps = {
  item: FAQItem;
  expanded: boolean;
  onChange: (event: React.MouseEvent) => void;
};

function FaqItem({ item, expanded, onChange }: FaqItemProps) {
  return (
    <div
      className={twMerge(
        'border border-violet rounded-[24px] bg-violet/10 overflow-hidden transition-all duration-300'
      )}
    >
      <div
        className="flex items-center justify-between p-4 md:py-5 md:px-10 cursor-pointer"
        onClick={onChange}
        aria-controls={`faq-${item.id}-content`}
        id={`faq-${item.id}-header`}
        aria-expanded={expanded}
      >
        <h3 className="md:text-[24px] font-medium text-violet">{item.question}</h3>
        <Iconify
          icon={expanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
          className="text-violet transition-transform duration-300"
          width={24}
        />
      </div>

      <AnimatePresence initial={false}>
        {expanded && (
          <m.div
            key={`faq-${item.id}-content`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div
              className="p-4 md:px-10 pt-0 text-violet text-sm md:text-lg [&>p]:mb-4 [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-4"
              dangerouslySetInnerHTML={{ __html: item.answer }}
            />
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
